import React from "react";
import moment from "moment";
import { makeStyles } from "@material-ui/core";
import clsx from "clsx";

import LargeToolTip from "../../../../account/components/LargeToolTip";

export const getBackgroundFromEventStatus = (status) => {
  const colorMap = {
    new: "#9bd8eb",
    disconnected: "#eee",
    available: "#337ab7",
    live: "#d9534f",
    "on break": "#f0ad4e",
    publishing: "#f0ad4e",
    unpublishing: "#f0ad4e",
    starting: "#f0ad4e",
    published: "#5cb85c",
  };
  const defaultColor = "#333";
  return colorMap[status] || defaultColor;
};

const getColorFromEventStatus = (status) => {
  const whiteStatuses = ["disconnected", "live", "available", "stopping"];
  return whiteStatuses.includes(status) ? "white" : "black";
};

const useEventWrapperStyles = makeStyles((theme) => ({
  monthDayEventWrapper: {
    borderRadius: "5px",
    borderRight: "2px solid #eee",
    lineHeight: "1",

    "&:hover": {
      zIndex: 2, // makes sure the events time block is visible on hover but still under the time indicator of zIndex: 3
    },
  },

  ellipsisLineWrapEventTitle: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "pre",
    color: "#333",
    padding: "5px 6px 0px 6px",
    marginBottom: "4px",

    flex: 1,
  },

  eventTime: {
    color: "#333",
    fontSize: "12px",
    margin: "0",
    paddingLeft: "6px",
  }, 

  eventStatusCircle: {
    width: "5px",
    height: "5px",
    borderRadius: "50%",
    backgroundColor: (props) => getBackgroundFromEventStatus(props.data.eventStatus),
    marginRight: "6px",
  },

  monthView: {
    "& .rbc-event": {
      backgroundColor: (props) => getBackgroundFromEventStatus(props.data.eventStatus),
      color: (props) => getColorFromEventStatus(props.data.eventStatus),
    },
  },
}));

const getTimeLabel = (label) => {
  // for labels like "12:10 PM - 12:10 PM" just show "12:10 PM"
  return label &&
    (label.substring(0, 8) === label.substring(11) ||
      label.substring(0, 7) === label.substring(10))
    ? label.substring(0, label.indexOf("M") + 1)
    : label;
};

const getPositioning = ({ event, getEvents }) => {
  // if there is an earlier event that started within 15 minutes offset this event to the left and split the width
  // because events use the entire width by default for the day view
  let nearbyEvents = [];
  let earlierEvents = [];

  const events = getEvents();
  const eventIndex = Object.values(events).findIndex(
    (ev) => ev.eventID === event.id
  );

  Object.values(events).forEach((ev, i) => {
    const eventTime = moment(event.start);
    if (ev.eventID === event.id) return;
    if (
      eventTime.isBetween(
        moment(ev.startDateTime).subtract(16, "minutes"),
        moment(ev.startDateTime).add(16, "minutes")
      )
    ) {
      // is nearby
      nearbyEvents.push(ev);
      if (moment(ev.startDateTime).isBefore(eventTime)) {
        earlierEvents.push(ev);
      }
      // if the events are at the same time earlier events are just the events iterated earlier
      if (moment(ev.startDateTime).isSame(eventTime) && i < eventIndex) {
        earlierEvents.push(ev);
      }
    }
  });

  // split width by nearby events
  // add width to left position for # of earlier events
  const width = `${100 / (nearbyEvents.length + 1)}%`;
  const left = `${earlierEvents.length * (100 / (nearbyEvents.length + 1))}%`;
  return { width, left };
};

const EventWrapper = (props) => {
  const { label, event, onClick, onKeyPress, onDoubleClick, type, getEvents } =
    props;

  const classes = useEventWrapperStyles(event);
  if (type === "date") {
    // only override the day "time" views
    // this is what gets returned for each event in the month view
    return (
      <div className={`${classes.monthDayEventWrapper} ${classes.monthView}`}>
        {props.children}
      </div>
    );
  }

  const time = getTimeLabel(label);

  const incomingStyles = props.style || {};
  if (incomingStyles.top) {
    incomingStyles.top = incomingStyles.top + "%";
  }

  const { width, left } = getPositioning({ event, getEvents });

  const pastelColors = [
    "hsl(43, 87%, 88%, 0.7)",
    "hsl(346, 94%, 94%, 0.7)",
    "hsl(60, 100%, 93%, 0.7)",
    "hsl(120, 18%, 88%, 0.7)",
    "hsl(0, 12%, 93%, 0.7)",
    "hsl(229, 53%, 83%, 0.7)",
    "hsl(33, 69%, 85%, 0.7)",
    "hsl(210, 52%, 87%, 0.7)",
    "hsl(357, 100%, 89%, 0.7)",
    "hsl(354, 63%, 97%, 0.7)",
    "hsl(343, 74%, 82%, 0.7)",
    "hsl(323, 38%, 88%, 0.7)",
    "hsl(150, 14%, 97%, 0.7)",
    "hsl(183, 18%, 75%, 0.7)",
    "hsl(227, 91%, 91%, 0.7)",
  ];

  // gets a random color from the pastel colors array
  const randomSemiTransparentColor = pastelColors[Math.floor(Math.random() * pastelColors.length)];

  // sets the random color and the eventID into local storage so the color is persistent across refreshes
  let calendarEventColors = JSON.parse(localStorage.getItem("calendarEventColors"));

  if (!calendarEventColors) {
    calendarEventColors = {
      [event.id]: randomSemiTransparentColor,
    };
  }

  if (!calendarEventColors[event.id]) {
    calendarEventColors[event.id] = randomSemiTransparentColor;
  }

  localStorage.setItem("calendarEventColors", JSON.stringify(calendarEventColors));

  const backgroundColor = calendarEventColors[event.id];

  return (
      <div
          onClick={onClick}
          onKeyPress={onKeyPress}
          onDoubleClick={onDoubleClick}
          className={clsx("calendar-event", classes.monthDayEventWrapper)}
          style={{
              ...incomingStyles,
              cursor: "pointer",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "pre",
              backgroundColor,
              color: "white",
              borderRadius: "5px",
              minHeight: "20px",
              position: "absolute",
              left,
              width,
          }}
      >
          <LargeToolTip title={event.title}>
              <div>
                  <div
                      style={{
                          display: "flex",
                          alignItems: "center",
                      }}
                  >
                      <div className={classes.ellipsisLineWrapEventTitle}>
                          {event.title}
                      </div>
                      <div className={classes.eventStatusCircle} />
                  </div>

                  <p className={classes.eventTime}>{time}</p>
              </div>
          </LargeToolTip>
      </div>
  );
};

export default EventWrapper;
